import React from 'react';
import { motion } from 'framer-motion';
import '../../../../Stylesheets/Dashboard/Other/AboutUs.css';
import { Link } from 'react-router-dom';
import { FaTools, FaHardHat, FaChartLine, FaBolt, FaWhatsapp } from 'react-icons/fa';

const pillars = [
  { icon: <FaHardHat />, title: 'Built for Site Teams', desc: 'Fast, offline-friendly UX for foremen & engineers on the ground.' },
  { icon: <FaTools />, title: 'Modular by Design', desc: 'Start with Projects, add Labor, Materials, Expenses as you grow.' },
  { icon: <FaChartLine />, title: 'Data-Driven Decisions', desc: 'Dashboards, KPIs, and soon—AI forecasting for costs & usage.' },
  { icon: <FaBolt />, title: 'Automations', desc: 'Recurring ingestions, smart alerts, and WhatsApp notifications.' },
];

const future = [
  { icon: <FaWhatsapp />, title: 'WhatsApp Alerts', blurb: 'Instant updates for materials, attendance, and expenses.' },
  { icon: <FaChartLine />, title: 'AI Forecasting', blurb: 'Predictive costs, overruns, and usage trends with ML.' },
  { icon: <FaTools />, title: 'Public Project Sharing', blurb: 'Share a read-only project summary securely with clients.' },
];

const AboutUs = () => {
  return (
    <div className="about-wrapper">
      {/* Hero */}
      <motion.section
        className="about-hero"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="about-hero-text">
          <h1>About <span>Site360</span></h1>
          <p>
            We’re building a simple, powerful operating system for construction teams—so
            small contractors, site engineers, and builders can run projects with the clarity
            of an enterprise tool, minus the bloat.
          </p>

          <div className="about-cta">
            <Link to="/dashboard/Whats-new" className="about-btn primary">What’s New</Link>
            <Link to="/dashboard/Contact-us" className="about-btn ghost">Talk to Us</Link>
          </div>
        </div>

        {/* Replace the image path below with your local asset if needed */}
        <div className="about-hero-media">
          <img
            src="/assets/civil-hero.jpg"
            alt="Civil engineer on site"
            onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?q=80&w=1600&auto=format&fit=crop'; }}
          />
        </div>
      </motion.section>

      {/* Mission */}
      <section className="about-mission">
        <div className="about-mission-card">
          <h3>Our Mission</h3>
          <p>
            Make construction ops **effortless**—from daily logs and attendance to materials,
            expenses, and analytics—so teams spend less time in spreadsheets and more time
            building. Site360 is crafted by product folks who obsess over speed, clarity,
            and reliability on real job sites.
          </p>
        </div>

        <div className="about-stats">
          <div><h2>5+</h2><p>Core Modules</p></div>
          <div><h2>60s</h2><p>Avg. Setup Time</p></div>
          <div><h2>99.9%</h2><p>Planned Uptime</p></div>
          <div><h2>AI-Ready</h2><p>Forecasting & Insights</p></div>
        </div>
      </section>

      {/* Product pillars */}
      <section className="about-pillars">
        <h3>What We Stand For</h3>
        <div className="pillar-grid">
          {pillars.map((p, i) => (
            <motion.div key={i} className="pillar-card"
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <div className="pillar-icon">{p.icon}</div>
              <h4>{p.title}</h4>
              <p>{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Roadmap snapshot */}
      <section className="about-future">
        <h3>What’s Coming Next</h3>
        <div className="future-grid">
          {future.map((f, i) => (
            <motion.div key={i} className="future-card"
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
              <div className="future-icon">{f.icon}</div>
              <h4>{f.title}</h4>
              <p>{f.blurb}</p>
            </motion.div>
          ))}
        </div>
        <p className="future-note">
          Want early access? <Link to="/dashboard/Contact-us">Ping us</Link> and we’ll add you to the beta.
        </p>
      </section>

      {/* CTA */}
      <section className="about-cta-wide">
        <h3>Ready to streamline your next project?</h3>
        <div className="cta-actions">
          <Link to="/dashboard/create" className="about-btn primary">Create Project</Link>
          <Link to="/dashboard/How-to-use" className="about-btn ghost">How to Use</Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
